import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  changeExchangeRateStatus,
  createExchangeRate,
  deleteExchangeRate,
  getExchangeRates,
  updateExchangeRate,
} from "../services/exchangeRateAdminService";
import { ExchangeRate } from "../types/admin";
import { ApiResponse } from "../types/common";

import {
  CreateExchangeRateRequest,
  UpdateExchangeRateRequest,
} from "../types/admin";

export const exchangeRateAdminKeys = {
  all: ["exchange-rates-admin"] as const,
  list: () => [...exchangeRateAdminKeys.all, "list"] as const,
  detail: (id: string) => [...exchangeRateAdminKeys.all, id] as const,
};

export const useExchangeRates = () => {
  return useQuery<ApiResponse<ExchangeRate[]>>({
    queryKey: exchangeRateAdminKeys.list(),
    queryFn: () => getExchangeRates(),
    staleTime: 30 * 1000,
  });
};

export const useExchangeRateMutations = () => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: exchangeRateAdminKeys.list() });
  };

  const create = useMutation({
    mutationFn: (data: CreateExchangeRateRequest) =>
      createExchangeRate(data),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateExchangeRateRequest }) =>
      updateExchangeRate(id, data),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteExchangeRate(id),
    onSuccess: invalidate,
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      changeExchangeRateStatus(id, isActive),
    onSuccess: invalidate,
  });

  return { create, update, remove, changeStatus };
};

